import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Send, Loader2, Check, X, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { fetchStateTrimestre } from '../lib/api';
import { useCoachChat } from '../lib/useCoachChat';
import CoachStatePanel from './nav/CoachStatePanel';
import CoachStatePanelSkeleton from './nav/CoachStatePanelSkeleton';
import ChatSkeleton from './nav/ChatSkeleton';
import MemoryChip from './nav/MemoryChip';
import CoachMarkdown from './CoachMarkdown';
import EndSessionButton from './nav/EndSessionButton';
import OpFieldsList from './nav/OpFieldsList';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';

const ENTITA_LABEL = {
  anno: 'Anno', numero_importante: 'Numero importante', obiettivo: 'Obiettivo',
  key_result: 'Key Result', target_periodo: 'Target di periodo', azione: 'Azione settimanale',
  mit: 'MIT', review: 'Review', capacita: 'Capacità', revisione: 'Revisione',
};
const AZIONE_LABEL = { crea: 'Crea', aggiorna: 'Aggiorna', chiudi: 'Chiudi' };

const QUARTER_OF_MONTH = { 1:1,2:1,3:1,4:2,5:2,6:2,7:3,8:3,9:3,10:4,11:4,12:4 };

function currentQuarterId() {
  const d = new Date();
  return `${d.getFullYear()}-Q${QUARTER_OF_MONTH[d.getMonth() + 1]}`;
}

function shiftQuarter(qid, delta) {
  const [ys, qs] = qid.split('-Q');
  let y = parseInt(ys, 10);
  let q = parseInt(qs, 10) + delta;
  while (q > 4) { q -= 4; y += 1; }
  while (q < 1) { q += 4; y -= 1; }
  return `${y}-Q${q}`;
}

export default function QuarterlyPlanChat() {
  const [quarter, setQuarter] = useState(currentQuarterId());

  const {
    stato,
    aree,
    messages,
    input,
    setInput,
    sending,
    applying,
    pendingOps,
    editingOpsJson,
    setEditingOpsJson,
    highlightIds,
    scrollRef,
    inputRef,
    handleSend,
    handleKeyDown,
    handleConfirmOps,
    handleCancelOps,
    handleCloseItem,
    handleUpdateItem,
    endSession,
  } = useCoachChat({
    momento: 'trimestre',
    fetchStateFn: fetchStateTrimestre,
    param: quarter,
    resumeGreetingText: (s) => `Riprendiamo dal trimestre ${s.quarter_label}.`,
  });

  if (!stato) {
    return (
      <div className="min-h-screen bg-canvas text-ink flex flex-col">
        <div className="flex-1 max-w-[1600px] w-full mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[1fr_480px] gap-8">
          <ChatSkeleton />
          <CoachStatePanelSkeleton title="Stato del trimestre" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas text-ink flex flex-col">
      {/* TOP BAR */}
      <div className="border-b border-line shrink-0">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-soft hover:text-ink transition-colors"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft size={14} strokeWidth={1.5} /> Dashboard
            </Link>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-mute">/</span>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-ink">Pianificazione trimestrale</span>
          </div>
          {/* Quarter switcher */}
          <div className="flex items-center gap-2" data-testid="quarter-switcher">
            <button
              onClick={() => setQuarter((q) => shiftQuarter(q, -1))}
              className="h-8 w-8 flex items-center justify-center text-soft hover:bg-ink hover:text-canvas transition-colors"
              data-testid="quarter-prev"
              aria-label="Quarter precedente"
            >
              <ChevronLeft size={14} strokeWidth={1.5} />
            </button>
            <span className="font-display text-xl tracking-tight px-3" data-testid="quarter-label">{stato.quarter_label}</span>
            <button
              onClick={() => setQuarter((q) => shiftQuarter(q, +1))}
              className="h-8 w-8 flex items-center justify-center text-soft hover:bg-ink hover:text-canvas transition-colors"
              data-testid="quarter-next"
              aria-label="Quarter successivo"
            >
              <ChevronRight size={14} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      {/* MAIN: 2 colonne */}
      <div className="flex-1 max-w-[1600px] w-full mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[1fr_480px] gap-8">
        {/* LEFT — Chat */}
        <div className="flex flex-col min-h-0 border border-line bg-paper">
          <div className="flex items-baseline justify-between px-6 py-4 border-b border-line">
            <h2 className="font-display text-lg tracking-tight">QuantumCoach · Trimestre</h2>
            <div className="flex items-center gap-3">
              <EndSessionButton onEnd={endSession} testid="trimestre-end-session-btn" />
              <span className="mono-label">claude · opus</span>
            </div>
          </div>

          {/* INPUT in alto (sticky) */}
          <div className="border-b border-line p-4 sticky top-0 bg-paper z-10">
            <div className="relative">
              <Textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Consuntivo del quarter, target nuovi, priorità 2-3 per il trimestre — una alla volta."
                className="rounded-none border-line focus-visible:ring-0 focus-visible:border-ink resize-none font-body text-base pr-14 min-h-[88px]"
                disabled={sending}
                data-testid="chat-input"
              />
              <button
                onClick={() => handleSend()}
                disabled={sending || !input.trim()}
                className="absolute bottom-3 right-3 h-9 w-9 bg-ink text-canvas flex items-center justify-center hover:bg-accent disabled:bg-mute transition-colors"
                data-testid="send-message-btn"
                aria-label="Invia"
              >
                <Send size={14} strokeWidth={1.8} />
              </button>
            </div>
            {sending && (
              <div className="flex items-center gap-2 text-mute font-mono text-xs mt-3">
                <Loader2 size={14} className="animate-spin" strokeWidth={1.5} />
                {messages.length === 0
                  ? 'QuantumCoach sta preparando il punto della situazione…'
                  : 'QuantumCoach sta pensando…'}
              </div>
            )}
          </div>

          {/* STORICO — reverse-chrono */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-6" data-testid="chat-messages">
            <MemoryChip />
            {[...messages].reverse().map((m, revIdx) => {
              if (m.hidden) return null;
              const idx = messages.length - 1 - revIdx;
              const isLast = idx === messages.length - 1;
              return (
                <MessageBubble
                  key={idx}
                  message={m}
                  isPending={pendingOps && pendingOps.msgIdx === idx}
                  editingJson={pendingOps && pendingOps.msgIdx === idx ? editingOpsJson : null}
                  setEditingJson={setEditingOpsJson}
                  applying={applying}
                  onConfirm={handleConfirmOps}
                  onCancel={handleCancelOps}
                  onEdit={() => setEditingOpsJson(JSON.stringify(pendingOps.operazioni, null, 2))}
                  onChipClick={isLast && !sending ? handleSend : null}
                  aree={aree}
                />
              );
            })}
          </div>
        </div>

        {/* RIGHT — Stato Trimestre */}
        <CoachStatePanel
          title="Stato del trimestre"
          subtitle={stato.quarter_label}
          obiettivi={stato.obiettivi || []}
          keyResults={stato.key_results || []}
          azioni={stato.azioni || []}
          mit={stato.mit || []}
          storageKey="quarterly"
          testid="quarter-panel"
          highlightIds={highlightIds}
          onCloseItem={handleCloseItem}
          onUpdateItem={handleUpdateItem}
        />
      </div>
    </div>
  );
}

function MessageBubble({ message, isPending, editingJson, setEditingJson, applying, onConfirm, onCancel, onEdit, onChipClick = null, aree = [] }) {
  if (message.isDivider) {
    return (
      <div className="flex items-center gap-3 my-4 select-none" data-testid="msg-divider" aria-hidden="true">
        <div className="flex-1 h-px bg-line" />
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-mute whitespace-nowrap">
          {(message.content || '').replace(/—/g, '').trim() || 'Sezione precedente'}
        </span>
        <div className="flex-1 h-px bg-line" />
      </div>
    );
  }
  if (message.role === 'user') {
    return (
      <div className="flex justify-end" data-testid="msg-user">
        <div className="max-w-[80%] bg-ink text-canvas px-5 py-3 font-body text-sm leading-relaxed">{message.content}</div>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="msg-assistant">
      <div className="flex items-baseline gap-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent mt-1">QuantumCoach</span>
        <div className="flex-1 min-w-0">
          <CoachMarkdown content={message.content} aree={aree} />
        </div>
      </div>

      {!isPending && !message.applied && !message.cancelled && Array.isArray(message.suggerimenti) && message.suggerimenti.length > 0 && onChipClick && (
        <div className="ml-[58px] flex flex-wrap gap-2" data-testid="quick-replies">
          {message.suggerimenti.map((s, i) => (
            <button
              key={`${i}-${s}`}
              type="button"
              onClick={() => onChipClick(s)}
              className="border border-line hover:border-accent hover:bg-accent/5 px-3 py-1.5 font-mono text-[11px] text-soft hover:text-accent transition-colors"
              data-testid={`quick-reply-${i}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {message.operazioni && message.operazioni.length > 0 && (
        <div className="ml-[58px] border-l-2 border-accent pl-4 py-2 bg-accent/5" data-testid="pending-operations">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={12} strokeWidth={1.5} className="text-accent" />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-accent">Sto per salvare</span>
          </div>

          {editingJson !== null ? (
            <Textarea
              value={editingJson}
              onChange={(e) => setEditingJson(e.target.value)}
              className="rounded-none font-mono text-xs border-line focus-visible:ring-0 focus-visible:border-accent min-h-[200px] bg-paper"
              data-testid="ops-edit-json"
            />
          ) : (
            <ul className="space-y-2 mb-4">
              {message.operazioni.map((op, i) => (
                <li key={op.id || `${op.entita}-${op.azione}-${i}`} className="font-mono text-xs leading-relaxed" data-testid="pending-op-item">
                  <span className="inline-block bg-ink text-canvas px-1.5 py-0.5 mr-2 uppercase tracking-wider text-[10px]">{AZIONE_LABEL[op.azione] || op.azione}</span>
                  <span className="text-soft">{ENTITA_LABEL[op.entita] || op.entita}</span>
                  {op.id && <span className="text-mute"> · {op.id.slice(0, 8)}…</span>}
                  <OpFieldsList campi={op.campi} />
                </li>
              ))}
            </ul>
          )}

          {isPending && (
            <div className="flex gap-2 items-center pt-2">
              <Button onClick={onConfirm} disabled={applying} className="rounded-none bg-ink hover:bg-accent text-canvas font-mono text-xs uppercase tracking-[0.2em] h-8 px-4" data-testid="confirm-ops-btn">
                {applying ? <Loader2 size={12} className="animate-spin mr-2" /> : <Check size={12} strokeWidth={1.8} className="mr-2" />}
                Conferma
              </Button>
              <button onClick={onCancel} disabled={applying} className="font-mono text-xs uppercase tracking-[0.2em] text-soft hover:text-ink px-3 h-8 flex items-center" data-testid="cancel-ops-btn">
                <X size={12} strokeWidth={1.8} className="mr-2" /> Annulla
              </button>
              <button onClick={onEdit} disabled={applying || editingJson !== null} className="font-mono text-xs uppercase tracking-[0.2em] text-soft hover:text-ink px-3 h-8 flex items-center disabled:opacity-40" data-testid="edit-ops-btn">
                Modifica
              </button>
            </div>
          )}
        </div>
      )}

      {message.applied && (
        <div className="ml-[58px] border-l-2 border-ok pl-4 py-2 bg-ok/10" data-testid="applied-summary">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ok mb-2"><Check className="inline h-3 w-3 mr-1" strokeWidth={1.5} /> Salvato</div>
          {Array.isArray(message.appliedSummary) && message.appliedSummary.length > 0 && (
            <ul className="space-y-1">
              {message.appliedSummary.map((s, i) => (
                <li key={`${i}-${s}`} className="font-body text-xs text-ink">{s}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {message.cancelled && <div className="ml-[58px] font-mono text-[10px] uppercase tracking-[0.2em] text-mute">Annullato</div>}
    </div>
  );
}
